import React, { useState } from 'react'
import {
  Shield,
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronRight,
  FileText,
  Eye,
  CheckCircle,
  Calendar,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'

interface AuditEntry {
  id: string
  timestamp: Date
  userId: string
  userName: string
  queryText: string
  responsePreview: string
  citations: Array<{
    manual: string
    section: string
    pages: string
  }>
  aircraftType?: string
  serviceCenter?: string
  sourceType: 'native' | 'external'
  responseTimeMs: number
  signatureValid: boolean
}

const sampleAuditLog: AuditEntry[] = [
  {
    id: '1',
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    userId: 'u1',
    userName: 'John Martinez',
    queryText: 'What is the torque specification for the B737-800 main landing gear axle nut?',
    responsePreview: 'The main landing gear axle nut torque specification for the B737-800 is 450-500 ft-lbs...',
    citations: [
      { manual: 'B737 AMM', section: '32-11-01', pages: '201-203' },
      { manual: 'B737 AMM', section: '32-11-01', pages: '204' },
    ],
    aircraftType: 'B737-800',
    sourceType: 'native',
    responseTimeMs: 1234,
    signatureValid: true,
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 1000 * 60 * 15),
    userId: 'u2',
    userName: 'Sarah Chen',
    queryText: 'A320 engine oil level check procedure',
    responsePreview: 'To check the engine oil level on the A320, follow these steps: 1. Ensure the engine has been...',
    citations: [
      { manual: 'A320 AMM', section: '79-00-00', pages: '101-102' },
    ],
    aircraftType: 'A320',
    sourceType: 'native',
    responseTimeMs: 987,
    signatureValid: true,
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 1000 * 60 * 32),
    userId: 'u3',
    userName: 'Mike Thompson',
    queryText: 'B777 tire pressure requirements for main gear',
    responsePreview: 'The B777-300ER main gear tire pressure specification is 215 PSI at ambient temperature...',
    citations: [
      { manual: 'B777 AMM', section: '32-41-00', pages: '501-503' },
    ],
    aircraftType: 'B777-300ER',
    sourceType: 'external',
    responseTimeMs: 1567,
    signatureValid: true,
  },
  {
    id: '4',
    timestamp: new Date(Date.now() - 1000 * 60 * 60),
    userId: 'u1',
    userName: 'John Martinez',
    queryText: 'Hydraulic fluid type for B737',
    responsePreview: 'The B737 aircraft uses MIL-PRF-83282 hydraulic fluid for all hydraulic systems...',
    citations: [
      { manual: 'B737 AMM', section: '29-00-00', pages: '1-5' },
    ],
    aircraftType: 'B737-800',
    sourceType: 'native',
    responseTimeMs: 876,
    signatureValid: true,
  },
  {
    id: '5',
    timestamp: new Date(Date.now() - 1000 * 60 * 90),
    userId: 'u2',
    userName: 'Sarah Chen',
    queryText: 'A320 flap rigging procedure',
    responsePreview: 'The flap rigging procedure for the A320 requires the following: 1. Position flaps to 0 degrees...',
    citations: [
      { manual: 'A320 AMM', section: '27-50-00', pages: '301-310' },
      { manual: 'A320 AMM', section: '27-50-00', pages: '311-315' },
    ],
    aircraftType: 'A320',
    sourceType: 'native',
    responseTimeMs: 1123,
    signatureValid: true,
  },
]

export function AuditLogPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState('7d')

  const formatTime = (date: Date) => {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const filteredLogs = sampleAuditLog.filter(
    (log) =>
      log.queryText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.aircraftType?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-600">
            FAA/EASA compliant query history with tamper-proof signatures
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            Compliance Report
          </Button>
        </div>
      </div>

      {/* Compliance Stats */}
      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-green-100 p-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">100%</div>
                <div className="text-sm text-gray-500">Signatures Valid</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-blue-100 p-2">
                <Shield className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">2,847</div>
                <div className="text-sm text-gray-500">Total Records</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-purple-100 p-2">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">7 years</div>
                <div className="text-sm text-gray-500">Retention Period</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-orange-100 p-2">
                <Calendar className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">5 min</div>
                <div className="text-sm text-gray-500">Last Activity</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search queries, users, aircraft..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <Button variant="outline" size="sm">
            <Filter className="mr-2 h-4 w-4" />
            More Filters
          </Button>
        </div>
      </div>

      {/* Audit Log Table */}
      <Card>
        <div className="overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="w-8"></th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Query
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Aircraft
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Service Center
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Signature
                </th>
              </tr>
            </thead>
            <tbody className="divide-y bg-white">
              {filteredLogs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    <td className="px-2">
                      <Button variant="ghost" size="icon" className="h-6 w-6">
                        {expandedId === log.id ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </Button>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-gray-900">
                        {formatTime(log.timestamp)}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm font-medium text-gray-900">
                        {log.userName}
                      </span>
                    </td>
                    <td className="px-4 py-4 max-w-md">
                      <p className="text-sm text-gray-900 truncate">
                        {log.queryText}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      {log.aircraftType && (
                        <Badge variant="secondary">{log.aircraftType}</Badge>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-4">
                      {log.serviceCenter ? (
                        <span className="text-sm text-gray-600">{log.serviceCenter}</span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <Badge variant={log.sourceType === 'native' ? 'info' : 'outline'}>
                        {log.sourceType}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      {log.signatureValid ? (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="h-4 w-4" />
                          <span className="text-xs">Valid</span>
                        </div>
                      ) : (
                        <Badge variant="destructive">Invalid</Badge>
                      )}
                    </td>
                  </tr>
                  {expandedId === log.id && (
                    <tr className="bg-gray-50">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid gap-4 lg:grid-cols-2">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Full Query
                            </h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                              {log.queryText}
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Response Preview
                            </h4>
                            <p className="text-sm text-gray-600 bg-white p-3 rounded-lg border">
                              {log.responsePreview}
                            </p>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Citations ({log.citations.length})
                            </h4>
                            <div className="space-y-2">
                              {log.citations.map((cit, i) => (
                                <div
                                  key={i}
                                  className="flex items-center gap-2 text-sm bg-white p-2 rounded-lg border"
                                >
                                  <FileText className="h-4 w-4 text-gray-400" />
                                  <span>
                                    {cit.manual} {cit.section}, Pages {cit.pages}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">
                              Metadata
                            </h4>
                            <div className="bg-white p-3 rounded-lg border space-y-1 text-sm">
                              <div className="flex justify-between">
                                <span className="text-gray-500">Response Time:</span>
                                <span className="font-medium">{log.responseTimeMs}ms</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">User ID:</span>
                                <span className="font-mono text-xs">{log.userId}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-500">Record ID:</span>
                                <span className="font-mono text-xs">{log.id}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex gap-2">
                          <Button variant="outline" size="sm">
                            <Eye className="mr-2 h-4 w-4" />
                            View Full Response
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="mr-2 h-4 w-4" />
                            Export Record
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {filteredLogs.length} of 2,847 records
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled>
            Previous
          </Button>
          <Button variant="outline" size="sm">
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
